import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  listCodesParrainage,
  createCodeParrainage,
  updateCodeParrainage,
  deleteCodeParrainage,
  listCommissions,
  typePartenaireLabel,
  typePartenaireIcon,
  normalizeCode,
  TYPES_PARTENAIRE,
  type CodeParrainage,
  type CodeParrainageInput,
  type CommissionParrainage,
  type TypePartenaire,
} from '@/lib/parrainage';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Codes de parrainage & revendeurs.
// Créer / modifier des codes de parrainage pour chaque type de
// partenaire (revendeur, structure partenaire, commerce) : pourcentage
// de commission versée au parrain et nombre de mois gratuits offerts au
// filleul. Affiche aussi l'historique des commissions générées.
// ─────────────────────────────────────────────────────────────

interface UserHit {
  id: number;
  name: string;
  nomcommerce: string;
  email: string;
}

const EMPTY_FORM: CodeParrainageInput = {
  code: '',
  typePartenaire: 'revendeur',
  ownerId: null,
  partnerName: '',
  pourcentage: 10,
  moisGratuits: 0,
  actif: true,
  description: '',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

export default function SuperAdminParrainagePage() {
  const [codes, setCodes] = useState<CodeParrainage[]>([]);
  const [commissions, setCommissions] = useState<CommissionParrainage[]>([]);
  const [ownerNames, setOwnerNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CodeParrainage | null>(null);
  const [form, setForm] = useState<CodeParrainageInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const [userQuery, setUserQuery] = useState('');
  const [userHits, setUserHits] = useState<UserHit[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, comms] = await Promise.all([listCodesParrainage(), listCommissions()]);
      setCodes(list);
      setCommissions(comms);
      const ids = [...new Set(list.map((c) => c.ownerId).filter((n): n is number => !!n))];
      if (ids.length > 0) {
        const { data } = await supabase
          .from('users')
          .select('id, name, nomcommerce')
          .in('id', ids);
        const map: Record<number, string> = {};
        ((data as { id: number; name: string; nomcommerce: string }[] | null) || []).forEach((u) => {
          map[u.id] = u.nomcommerce || u.name || `#${u.id}`;
        });
        setOwnerNames(map);
      } else {
        setOwnerNames({});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les codes de parrainage.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const sum = commissions.reduce((acc, c) => acc + (c.commission || 0), 0);
    const byParrain = new Map<number, number>();
    commissions.forEach((c) => {
      if (c.parrainId) byParrain.set(c.parrainId, (byParrain.get(c.parrainId) || 0) + c.commission);
    });
    return { count: commissions.length, sum: Math.round(sum * 100) / 100, parrains: byParrain.size };
  }, [commissions]);

  const searchUsers = async (q: string) => {
    setUserQuery(q);
    const term = q.trim();
    if (term.length < 2) { setUserHits([]); return; }
    setSearchingUsers(true);
    try {
      const { data } = await supabase
        .from('users')
        .select('id, name, nomcommerce, email')
        .or(`name.ilike.%${term}%,nomcommerce.ilike.%${term}%,email.ilike.%${term}%,user_name.ilike.%${term}%`)
        .limit(8);
      setUserHits((data as UserHit[] | null) || []);
    } catch {
      setUserHits([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFormError('');
    setUserQuery('');
    setUserHits([]);
    setFormOpen(true);
  };

  const openEdit = (c: CodeParrainage) => {
    setEditing(c);
    setForm({
      code: c.code,
      typePartenaire: c.typePartenaire,
      ownerId: c.ownerId,
      partnerName: c.partnerName,
      pourcentage: c.pourcentage,
      moisGratuits: c.moisGratuits,
      actif: c.actif,
      description: c.description,
    });
    setFormError('');
    setUserQuery('');
    setUserHits([]);
    setFormOpen(true);
  };

  const save = async () => {
    if (!normalizeCode(form.code)) { setFormError('Le code est obligatoire.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editing) await updateCodeParrainage(editing.id, form);
      else await createCodeParrainage(form);
      setFormOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActif = async (c: CodeParrainage) => {
    setBusyId(c.id);
    setCodes((prev) => prev.map((x) => (x.id === c.id ? { ...x, actif: !x.actif } : x)));
    try {
      await updateCodeParrainage(c.id, { actif: !c.actif });
    } catch {
      setCodes((prev) => prev.map((x) => (x.id === c.id ? { ...x, actif: c.actif } : x)));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (c: CodeParrainage) => {
    setBusyId(c.id);
    try {
      await deleteCodeParrainage(c.id);
      setCodes((prev) => prev.filter((x) => x.id !== c.id));
    } catch {
      setError('Impossible de supprimer ce code.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Codes de parrainage</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Définissez les codes des revendeurs, structures et commerces : commission versée au parrain et mois gratuits du filleul.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          <i className="ri-add-line"></i>
          Nouveau code
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-share-circle-line text-lg"></i>
          </div>
          <div>
            <div className="text-xl font-bold font-heading text-foreground-950">{codes.length}</div>
            <div className="text-xs text-foreground-500">Codes créés</div>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-hand-coin-line text-lg"></i>
          </div>
          <div>
            <div className="text-xl font-bold font-heading text-foreground-950">{totals.sum.toLocaleString()}</div>
            <div className="text-xs text-foreground-500">Commissions versées ({totals.count})</div>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
            <i className="ri-user-star-line text-lg"></i>
          </div>
          <div>
            <div className="text-xl font-bold font-heading text-foreground-950">{totals.parrains}</div>
            <div className="text-xs text-foreground-500">Parrains actifs</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : codes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-share-circle-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucun code de parrainage. Créez le premier.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {codes.map((c) => (
            <div key={c.id} className={`bg-background-50 border rounded-lg p-5 ${c.actif ? 'border-background-200/70' : 'border-background-200/70 opacity-60'}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${c.actif ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'}`}>
                  <i className={`${typePartenaireIcon(c.typePartenaire)} text-xl`}></i>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-foreground-950 font-mono tracking-wide">{c.code}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${c.actif ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-600'}`}>
                      {c.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-0.5 flex items-center gap-1">
                    <i className="ri-price-tag-3-line"></i>
                    {typePartenaireLabel(c.typePartenaire)}
                    {(c.partnerName || (c.ownerId && ownerNames[c.ownerId])) && (
                      <span>· {c.partnerName || ownerNames[c.ownerId as number]}</span>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-secondary-100 text-secondary-900">
                  <i className="ri-percent-line"></i>
                  {c.pourcentage}% de commission
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-accent-100 text-accent-900">
                  <i className="ri-gift-line"></i>
                  {c.moisGratuits} mois gratuit{c.moisGratuits > 1 ? 's' : ''}
                </span>
              </div>

              {c.description && <p className="text-xs text-foreground-600 leading-relaxed mb-3 line-clamp-2">{c.description}</p>}

              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEdit(c)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors"
                >
                  <i className="ri-pencil-line"></i>
                  Modifier
                </button>
                <button
                  onClick={() => toggleActif(c)}
                  disabled={busyId === c.id}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 ${c.actif ? 'bg-accent-100 text-accent-900 hover:bg-accent-200' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}
                >
                  {busyId === c.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className={c.actif ? 'ri-eye-off-line' : 'ri-eye-line'}></i>}
                  {c.actif ? 'Désactiver' : 'Activer'}
                </button>
                <button
                  onClick={() => remove(c)}
                  disabled={busyId === c.id}
                  title="Supprimer le code"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Historique des commissions */}
      <div className="mt-10">
        <h3 className="text-base font-semibold text-foreground-950 mb-3 flex items-center gap-2">
          <i className="ri-history-line text-foreground-500"></i>
          Historique des commissions
        </h3>
        {commissions.length === 0 ? (
          <p className="text-sm text-foreground-500 py-6 text-center bg-background-50 border border-background-200/70 rounded-lg">
            Aucune commission enregistrée pour le moment.
          </p>
        ) : (
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-foreground-500 border-b border-background-200/70">
                    <th className="px-4 py-3 font-medium whitespace-nowrap">Date</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap">Parrain</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap">Filleul</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap">Code</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Montant</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.slice(0, 50).map((c) => (
                    <tr key={c.id} className="border-b border-background-100 last:border-0">
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">{formatDate(c.createdAt)}</td>
                      <td className="px-4 py-3 text-foreground-800 truncate max-w-[160px]">{c.parrainName || (c.parrainId ? ownerNames[c.parrainId] : '') || '—'}</td>
                      <td className="px-4 py-3 text-foreground-600 truncate max-w-[160px]">{c.filleulName || (c.filleulId ? `#${c.filleulId}` : '—')}</td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground-500 whitespace-nowrap">{c.code}</td>
                      <td className="px-4 py-3 text-right text-foreground-600 whitespace-nowrap">{c.montant.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-semibold text-primary-600 whitespace-nowrap">
                        {c.commission.toLocaleString()} <span className="text-[10px] text-foreground-400">({c.pourcentage}%)</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Formulaire */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setFormOpen(false)}>
          <div className="bg-background-50 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <h3 className="text-base font-semibold text-foreground-950">{editing ? 'Modifier le code' : 'Nouveau code de parrainage'}</h3>
              <button onClick={() => setFormOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Code</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: normalizeCode(e.target.value) })}
                    placeholder="REVAB12"
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm font-mono uppercase text-foreground-900 focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Type de partenaire</label>
                  <select
                    value={form.typePartenaire}
                    onChange={(e) => setForm({ ...form, typePartenaire: e.target.value as TypePartenaire })}
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                  >
                    {TYPES_PARTENAIRE.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Rattachement à un compte */}
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Rattacher à un compte (facultatif)</label>
                {form.ownerId ? (
                  <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-primary-50 border border-primary-200 rounded-lg">
                    <span className="text-sm text-foreground-800 truncate">
                      <i className="ri-user-line mr-1.5 text-primary-600"></i>
                      {ownerNames[form.ownerId] || `#${form.ownerId}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, ownerId: null })}
                      className="text-foreground-400 hover:text-red-600 cursor-pointer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={userQuery}
                      onChange={(e) => searchUsers(e.target.value)}
                      placeholder="Rechercher un utilisateur (nom, email)…"
                      className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                    />
                    {searchingUsers && <p className="text-[11px] text-foreground-400 mt-1 flex items-center gap-1"><i className="ri-loader-4-line animate-spin"></i>Recherche…</p>}
                    {userHits.length > 0 && (
                      <div className="mt-2 border border-background-200/70 rounded-lg divide-y divide-background-100 max-h-40 overflow-y-auto">
                        {userHits.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              setForm({ ...form, ownerId: u.id, partnerName: u.nomcommerce || u.name });
                              setOwnerNames((prev) => ({ ...prev, [u.id]: u.nomcommerce || u.name }));
                              setUserHits([]);
                              setUserQuery('');
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-background-100 cursor-pointer"
                          >
                            <span className="text-sm text-foreground-800 block truncate">{u.nomcommerce || u.name}</span>
                            <span className="text-[11px] text-foreground-400 block truncate">{u.email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Nom affiché du partenaire (facultatif)</label>
                <input
                  type="text"
                  value={form.partnerName}
                  onChange={(e) => setForm({ ...form, partnerName: e.target.value })}
                  placeholder="ex. Zifek Cotonou"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Commission (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.pourcentage}
                    onChange={(e) => setForm({ ...form, pourcentage: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Mois gratuits offerts</label>
                  <input
                    type="number"
                    min={0}
                    value={form.moisGratuits}
                    onChange={(e) => setForm({ ...form, moisGratuits: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Description (facultatif)</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 resize-none"
                />
              </div>

              <button
                type="button"
                onClick={() => setForm({ ...form, actif: form.actif === false })}
                className="w-full flex items-center justify-between p-3 bg-background-100 rounded-lg cursor-pointer"
              >
                <span className="text-xs font-medium text-foreground-700">{form.actif !== false ? 'Code actif' : 'Code inactif'}</span>
                <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${form.actif !== false ? 'bg-accent-500 justify-end' : 'bg-background-300/70 justify-start'}`}>
                  <span className="w-5 h-5 rounded-full bg-background-50 block"></span>
                </span>
              </button>

              {formError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}
            </div>

            <div className="flex gap-3 px-5 py-4 border-t border-background-200/70">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                {editing ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}